import type { Locale } from 'date-fns';
import {
  useCallback,
  type ChangeEvent,
  type ComponentType,
  useMemo,
} from 'react';
import {
  DateLib,
  UI,
  useDayPicker,
  type MonthCaptionProps,
} from 'react-day-picker';
import {
  buildMonthYearOptions,
  dateFromMonthValue,
  getNavigationRange,
  monthValueFromDate,
  visuallyHiddenStyles,
} from '../utils';

type BaseMonthCaption = ComponentType<MonthCaptionProps> | undefined;

type MonthCaptionConfig =
  | BaseMonthCaption
  | {
      BaseComponent?: BaseMonthCaption;
      monthNames?: readonly string[];
      localeCode?: string;
    };

export const createMonthCaptionComponent = (config?: MonthCaptionConfig) => {
  const { BaseComponent, monthNames, localeCode } =
    typeof config === 'function' || config === undefined
      ? {
          BaseComponent: config as BaseMonthCaption,
          monthNames: undefined,
          localeCode: undefined,
        }
      : (config ?? {});

  const MonthCaptionComponent = (props: MonthCaptionProps) => {
    const dayPicker = useDayPicker();
    const {
      dayPickerProps,
      goToMonth,
      classNames,
      styles,
      components,
      labels,
      formatters,
    } = dayPicker;

    const { calendarMonth, children, displayIndex: _, ...divProps } = props;

    const dateLib = useMemo(
      () =>
        new DateLib(
          {
            locale: dayPickerProps.locale as Locale | undefined,
            weekStartsOn: dayPickerProps.broadcastCalendar
              ? 1
              : dayPickerProps.weekStartsOn,
            firstWeekContainsDate: dayPickerProps.firstWeekContainsDate,
            useAdditionalWeekYearTokens:
              dayPickerProps.useAdditionalWeekYearTokens,
            useAdditionalDayOfYearTokens:
              dayPickerProps.useAdditionalDayOfYearTokens,
            timeZone: dayPickerProps.timeZone,
            numerals: dayPickerProps.numerals,
          },
          dayPickerProps.dateLib,
        ),
      [dayPickerProps],
    );

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLSelectElement>) => {
        const selectedValue = Number(event.target.value);

        if (Number.isNaN(selectedValue)) {
          return;
        }

        const month = dateFromMonthValue(dateLib, selectedValue);
        goToMonth(month);
      },
      [dateLib, goToMonth],
    );

    if (dayPickerProps.captionLayout !== 'dropdown') {
      if (BaseComponent) {
        return <BaseComponent {...props} />;
      }

      return <div {...divProps}>{children}</div>;
    }

    const currentMonth = dateLib.startOfMonth(calendarMonth.date);
    const [navStart, navEnd] = getNavigationRange(dayPickerProps, dateLib);
    const resolvedLocaleCode =
      localeCode ??
      (dayPickerProps.locale as { code?: string } | undefined)?.code ??
      '';
    const isChineseLocale = resolvedLocaleCode.toLowerCase().startsWith('zh');

    const options = buildMonthYearOptions(
      dateLib,
      navStart,
      navEnd,
      currentMonth,
    ).map((option) => {
      const optionDate = dateFromMonthValue(dateLib, option.value);
      const monthIndex = dateLib.getMonth(optionDate);
      const translatedMonthName = monthNames?.[monthIndex];
      const year = dateLib.getYear(optionDate);
      const monthYearOrder = dateLib.getMonthYearOrder();
      const label = isChineseLocale
        ? `${year}年${monthIndex + 1}月`
        : translatedMonthName === undefined
          ? option.label
          : monthYearOrder === 'year-first'
            ? `${year} ${translatedMonthName}`
            : `${translatedMonthName} ${year}`;

      return {
        ...option,
        label,
        disabled: option.disabled || Boolean(dayPickerProps.disableNavigation),
      };
    });

    if (options.length === 0) {
      if (BaseComponent) {
        return <BaseComponent {...props} />;
      }

      return <div {...divProps}>{children}</div>;
    }

    const captionText = formatters.formatCaption(
      currentMonth,
      dateLib.options,
      dateLib,
    );

    return (
      <div {...divProps}>
        <components.Dropdown
          aria-label={labels.labelMonthDropdown()}
          className={classNames[UI.MonthsDropdown]}
          classNames={classNames}
          components={components}
          disabled={Boolean(dayPickerProps.disableNavigation)}
          onChange={handleChange}
          options={options}
          style={styles?.[UI.Dropdown]}
          value={monthValueFromDate(dateLib, currentMonth)}
        />
        <span aria-live="polite" role="status" style={visuallyHiddenStyles}>
          {captionText}
        </span>
      </div>
    );
  };

  MonthCaptionComponent.displayName = 'DateRangePickerMonthCaption';

  return MonthCaptionComponent;
};
